import { useState, useEffect, useRef } from 'react'
import {
  Sparkles, Calendar, Heart, Settings, Search, MessageSquare,
  FileText, Clock, ChevronRight, Plus, X, User, LayoutGrid,
  Send, Mic, Paperclip, Compass, Bell, ChevronDown, LogIn, LogOut
} from 'lucide-react'
import { useBedrockChat } from './hooks/useBedrockChat'
import { useAuth } from './hooks/useAuth.jsx'
import { signInWithRedirect } from 'aws-amplify/auth'

function App() {
  const [activeTab, setActiveTab] = useState('chat')
  const [isCustomizing, setIsCustomizing] = useState(false)
  const [hubExpanded, setHubExpanded] = useState(true)
  const [agentInput, setAgentInput] = useState('')
  const chatContainerRef = useRef(null)
  const { sendMessage, isLoading, error } = useBedrockChat()
  const { user: authUser, isLoading: authLoading, isAuthenticated, signOut } = useAuth()

  // Generate greeting message based on auth state
  const getGreetingMessage = () => {
    if (isAuthenticated && authUser) {
      const firstName = authUser.givenName || authUser.name?.split(' ')[0] || 'there'
      return `Good morning, ${firstName}. I'm your Employee Concierge, powered by AI. I'm here to help you with HR requests, benefits, campus navigation, and much more. How can I help you today?`
    }
    return "Welcome to Navigator | Amplify 2.0. I'm your AI-powered Employee Concierge, here to help you with HR requests, benefits, campus navigation, and more. Please sign in for personalized assistance."
  }

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: getGreetingMessage()
    }
  ])

  // Update greeting when auth state changes
  useEffect(() => {
    if (!authLoading) {
      setMessages([{
        role: 'assistant',
        content: getGreetingMessage()
      }])
    }
  }, [isAuthenticated, authUser, authLoading])

  // User display info (from auth or mock)
  const displayUser = isAuthenticated && authUser ? {
    name: authUser.name || authUser.email?.split('@')[0] || 'User',
    role: authUser.attributes?.['custom:title'] || 'Vanderbilt Employee',
    avatar: (authUser.givenName?.[0] || '') + (authUser.familyName?.[0] || authUser.name?.[0] || 'U')
  } : {
    name: "Guest",
    role: "Sign in to continue",
    avatar: "G"
  }

  // Handle sign in
  const handleSignIn = async () => {
    try {
      await signInWithRedirect()
    } catch (err) {
      console.error('Sign in error:', err)
    }
  }

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign out error:', err)
    }
  }

  // Mock Resources (Your Hub items)
  const [resources, setResources] = useState([
    { id: 1, title: 'Holiday Calendar', icon: Calendar, content: 'Upcoming: Thanksgiving Break (Nov 27-29)', type: 'info', active: true },
    { id: 2, title: 'Benefits Portal', icon: Heart, content: 'Vanderbilt Health Plan Plus', type: 'link', active: true },
    { id: 3, title: 'Procurement', icon: FileText, content: '2 Approvals Pending', type: 'alert', active: true },
    { id: 4, title: 'Time & Absence', icon: Clock, content: 'PTO Balance: 120 Hours', type: 'info', active: true },
    { id: 5, title: 'Faculty Senate', icon: User, content: 'Meeting Minutes: Oct 2024', type: 'link', active: false },
    { id: 6, title: 'Research Grants', icon: Search, content: 'NSF Proposal Due in 14 days', type: 'alert', active: false },
  ])

  const toggleResource = (id) => {
    setResources(resources.map(res =>
      res.id === id ? { ...res, active: !res.active } : res
    ))
  }

  // Auto-scroll to bottom of chat (scroll only the chat container, not the page)
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, [messages, isLoading])

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!agentInput.trim() || isLoading) return

    const userMessage = agentInput.trim()
    setAgentInput('')

    // Add user message to chat
    const newMessages = [...messages, { role: 'user', content: userMessage }]
    setMessages(newMessages)

    try {
      // Format messages for the API (convert to Bedrock format)
      // Filter out any assistant messages before the first user message
      // (Claude requires the first message to be from the user)
      const firstUserIndex = newMessages.findIndex(msg => msg.role === 'user')
      const conversationMessages = firstUserIndex >= 0
        ? newMessages.slice(firstUserIndex)
        : newMessages

      const apiMessages = conversationMessages.map(msg => ({
        role: msg.role === 'assistant' ? 'assistant' : 'user',
        content: msg.content
      }))

      // Call Bedrock via our API
      const response = await sendMessage(apiMessages)

      setMessages(prev => [...prev, { role: 'assistant', content: response }])
    } catch (err) {
      // Error handling - show error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or contact IT support if the issue persists."
      }])
    }
  }

  const handleSuggestionClick = (suggestion) => {
    setAgentInput(suggestion)
  }

  // Navigation items
  const navItems = [
    { id: 'chat', icon: MessageSquare, label: 'Concierge' },
    { id: 'calendar', icon: Calendar, label: 'Schedule' },
    { id: 'docs', icon: FileText, label: 'Documents' },
  ]

  return (
    <div className="h-screen bg-[#050505] text-white font-sans-body selection:bg-gold selection:text-black overflow-hidden relative flex">

      {/* --- SIDEBAR --- */}
      <nav className="glass-nav w-20 lg:w-72 flex flex-col py-8 px-4 z-50 shrink-0 transition-all duration-300 overflow-y-auto">
        {/* Logo */}
        <div className="flex items-center gap-4 mb-8 pl-2">
          <div className="w-12 h-12 bg-gradient-to-br from-gold to-gold-dark rounded-xl flex items-center justify-center text-black font-condensed font-bold text-3xl shadow-[0_0_20px_rgba(207,174,112,0.3)]">
            V
          </div>
          <div className="hidden lg:block opacity-90">
            <h1 className="font-serif-display text-xl leading-none tracking-wide">VANDERBILT</h1>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-sans-body mt-1">Employee Navigator</p>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="space-y-2 mb-6">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-4 p-3.5 rounded-xl transition-all duration-300 group relative overflow-hidden
                ${activeTab === item.id
                  ? 'bg-white/10 text-white border border-white/10 shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
            >
              {activeTab === item.id && (
                <div className="absolute left-0 top-0 h-full w-1 bg-gold"></div>
              )}
              <item.icon className={`w-5 h-5 ${activeTab === item.id ? 'text-gold' : ''}`} />
              <span className="hidden lg:block font-sans-body text-sm font-medium">{item.label}</span>
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-white/10 mx-2 mb-4"></div>

        {/* Your Hub Section */}
        <div className="flex-1">
          <button
            onClick={() => setHubExpanded(!hubExpanded)}
            className="w-full flex items-center justify-between px-3 py-2 mb-2 text-gray-400 hover:text-white transition-colors"
          >
            <div className="flex items-center gap-3">
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden lg:block text-xs font-medium uppercase tracking-wider">Your Hub</span>
            </div>
            <ChevronDown className={`hidden lg:block w-4 h-4 transition-transform ${hubExpanded ? '' : '-rotate-90'}`} />
          </button>

          {hubExpanded && (
            <div className="space-y-1">
              {resources.filter(r => r.active).map((res) => {
                const IconComponent = res.icon
                return (
                  <div
                    key={res.id}
                    className="group relative flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-all cursor-pointer"
                  >
                    <div className={`p-2 rounded-lg ${
                      res.type === 'alert' ? 'bg-amber-500/10 text-amber-400' :
                      res.type === 'link' ? 'bg-blue-400/10 text-blue-300' :
                      'bg-white/5 text-gray-300'
                    }`}>
                      <IconComponent className="w-4 h-4" />
                    </div>
                    <div className="hidden lg:block flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-200 group-hover:text-white truncate">{res.title}</p>
                      <p className="text-xs text-gray-500 truncate">{res.content}</p>
                    </div>
                    {res.type === 'alert' && (
                      <span className="absolute right-2 top-2 w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                    )}
                    {isCustomizing && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleResource(res.id); }}
                        className="hidden lg:block absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-red-500/20 text-red-400 rounded-full hover:bg-red-500 hover:text-white transition-all opacity-0 group-hover:opacity-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )
              })}

              {/* Add items when customizing */}
              {isCustomizing && resources.filter(r => !r.active).map((res) => {
                const IconComponent = res.icon
                return (
                  <button
                    key={res.id}
                    onClick={() => toggleResource(res.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-white/20 hover:border-gold hover:bg-white/5 transition-all text-gray-500 hover:text-gold"
                  >
                    <div className="p-2 rounded-lg bg-white/5">
                      <IconComponent className="w-4 h-4" />
                    </div>
                    <span className="hidden lg:block text-sm">Add {res.title}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Settings / Customize */}
        <button
          onClick={() => setIsCustomizing(!isCustomizing)}
          className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all mt-4
            ${isCustomizing
              ? 'bg-gold/20 text-gold border border-gold/30'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
        >
          {isCustomizing ? <X className="w-5 h-5" /> : <Settings className="w-5 h-5" />}
          <span className="hidden lg:block text-sm font-medium">{isCustomizing ? 'Done Editing' : 'Customize'}</span>
        </button>

        {/* User Profile / Auth */}
        {authLoading ? (
          <div className="hidden lg:flex items-center justify-center p-4 mt-4">
            <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin"></div>
          </div>
        ) : isAuthenticated ? (
          <div className="hidden lg:block mt-4">
            <div className="flex items-center gap-3 p-4 rounded-xl glass-tile border border-white/5 group">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold/20 to-gold/10 border border-gold/30 flex items-center justify-center text-gold font-bold font-serif-display">
                {displayUser.avatar}
              </div>
              <div className="overflow-hidden flex-1">
                <p className="text-sm font-medium truncate text-gray-200">{displayUser.name}</p>
                <p className="text-xs text-gray-500 truncate">{displayUser.role}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 p-3 mt-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        ) : (
          <button
            onClick={handleSignIn}
            className="hidden lg:flex w-full items-center gap-3 p-4 mt-4 rounded-xl bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 transition-all group"
          >
            <LogIn className="w-5 h-5" />
            <span className="text-sm font-medium">Sign In</span>
          </button>
        )}

        {/* Mobile Sign In Button */}
        {!isAuthenticated && !authLoading && (
          <button
            onClick={handleSignIn}
            className="lg:hidden flex items-center justify-center p-3 mt-4 rounded-xl bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 transition-all"
          >
            <LogIn className="w-5 h-5" />
          </button>
        )}
      </nav>

      {/* --- MAIN CONTENT (Full-Pane Chat) --- */}
      <main className="flex-1 h-screen flex flex-col abstract-bg relative">

        {/* Abstract Visual Anchors for Glass Effect */}
        <div className="absolute top-[-200px] right-[-100px] w-[800px] h-[800px] bg-gold opacity-[0.08] rounded-full blur-[150px] pointer-events-none"></div>
        <div className="absolute bottom-[0px] left-[20%] w-[600px] h-[400px] bg-blue-900 opacity-[0.1] rounded-full blur-[120px] pointer-events-none"></div>

        {/* Chat Header */}
        <header className="flex justify-between items-center p-6 border-b border-white/5 relative z-10">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gold text-sm font-medium uppercase tracking-wider">
              <Sparkles className="w-4 h-4" />
              <span>AI Concierge</span>
            </div>
            <span className="text-gray-500">|</span>
            {isAuthenticated && authUser ? (
              <h2 className="text-xl font-serif-display text-white">
                Hello, <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-light">
                  {authUser.givenName || authUser.name?.split(' ')[0] || 'there'}
                </span>
              </h2>
            ) : (
              <h2 className="text-xl font-serif-display text-white">
                Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-light">Navigator</span> | Amplify 2.0
              </h2>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!isAuthenticated && !authLoading && (
              <button
                onClick={handleSignIn}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gold text-black font-medium text-sm hover:bg-gold-light transition-all"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">Sign In</span>
              </button>
            )}
            <button className="p-3 rounded-full glass-tile text-gray-300 hover:text-white relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.5)]"></span>
            </button>
          </div>
        </header>

        {/* Chat Messages Area */}
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar relative z-10">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[70%] rounded-2xl p-5 text-sm font-sans-body leading-7 shadow-lg backdrop-blur-md
                ${msg.role === 'user'
                  ? 'bg-gold/90 text-black font-medium rounded-br-sm'
                  : 'bg-white/10 text-gray-100 border border-white/10 rounded-bl-sm'
                }`}>
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-3 text-gold font-bold text-xs uppercase tracking-widest opacity-80">
                    <Sparkles className="w-3 h-3" />
                    Vanderbilt AI
                  </div>
                )}
                <div className="whitespace-pre-wrap">{msg.content}</div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white/5 rounded-2xl p-4 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce"></span>
                <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce delay-100"></span>
                <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce delay-200"></span>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mb-4 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm relative z-10">
            {error}
          </div>
        )}

        {/* Input Area */}
        <div className="p-6 border-t border-white/5 relative z-10">
          {/* Suggestions */}
          <div className="flex flex-wrap gap-3 mb-4">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider py-1.5">Suggestions:</span>
            {[
              "Draft FMLA Request",
              "Check Grant Balance",
              "Campus Shuttle Schedule",
              "IT Service Portal"
            ].map((action, i) => (
              <button
                key={i}
                onClick={() => handleSuggestionClick(action)}
                className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-gray-400 hover:bg-white/10 hover:border-gold/50 hover:text-gold transition-all backdrop-blur-sm"
              >
                {action}
              </button>
            ))}
          </div>

          {/* Input Bar */}
          <form onSubmit={handleSendMessage} className="relative">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-gold/50 to-blue-500/50 rounded-xl opacity-0 group-hover:opacity-50 transition duration-500 blur"></div>
              <div className="relative flex items-center bg-[#0f0f0f] border border-white/10 rounded-xl p-2 shadow-inner">
                <button type="button" className="p-3 text-gray-400 hover:text-gold transition-colors">
                  <Plus className="w-5 h-5" />
                </button>
                <input
                  type="text"
                  value={agentInput}
                  onChange={(e) => setAgentInput(e.target.value)}
                  placeholder="Ask about university policies, forms, or your schedule..."
                  className="w-full bg-transparent border-none text-white placeholder-gray-500 focus:ring-0 px-2 text-lg font-light outline-none"
                  disabled={isLoading}
                />
                <div className="flex items-center gap-2 pr-2">
                  <button type="button" className="p-2 text-gray-400 hover:text-white transition-colors">
                    <Mic className="w-5 h-5" />
                  </button>
                  <button
                    type="submit"
                    disabled={!agentInput.trim() || isLoading}
                    className={`p-2 rounded-lg transition-all duration-300
                      ${agentInput.trim() && !isLoading
                        ? 'bg-gold text-black shadow-[0_0_15px_rgba(207,174,112,0.5)]'
                        : 'bg-white/5 text-gray-600'
                      }`}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}

export default App
